// src/components/UserStats.tsx
import React, { useState, useEffect } from 'react';
import { UserStats } from '../types/chat';
import { analyticsApi } from '../services/api';

const UserStatsComponent: React.FC = () => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const userStats = await analyticsApi.getMyStats();
      setStats(userStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Loading your stats...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-destructive">Failed to load stats</p>
      </div>
    );
  }

  const { sessions, code_interactions } = stats;
  const copySuccessRate = code_interactions.total_code_blocks > 0 
    ? (code_interactions.copied_code_blocks / code_interactions.total_code_blocks * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      <h3 className="text-2xl font-bold text-foreground">Your Usage Statistics</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Sessions</h4>
          <div className="text-2xl font-bold text-foreground">{sessions.total_sessions || 0}</div>
          <div className="text-xs text-muted-foreground">Total Sessions</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Messages</h4>
          <div className="text-2xl font-bold text-foreground">{(sessions.avg_messages_per_session || 0).toFixed(1)}</div>
          <div className="text-xs text-muted-foreground">Avg per Session</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Code Blocks</h4>
          <div className="text-2xl font-bold text-foreground">{code_interactions.total_code_blocks || 0}</div>
          <div className="text-xs text-muted-foreground">Generated</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Success Rate</h4>
          <div className="text-2xl font-bold text-success">{copySuccessRate}%</div>
          <div className="text-xs text-muted-foreground">Code Copied</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Languages</h4>
          <div className="text-2xl font-bold text-foreground">{code_interactions.languages_used || 0}</div>
          <div className="text-xs text-muted-foreground">Different Types</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Total Cost</h4>
          <div className="text-2xl font-bold text-foreground">${(sessions.total_cost || 0).toFixed(4)}</div>
          <div className="text-xs text-muted-foreground">AWS Bedrock</div>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={loadStats}
          className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors"
        >
          Refresh Stats
        </button>
      </div>
    </div>
  );
};

export default UserStatsComponent;